import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Notification, View } from '../types';

import { getRoddoTheme } from '../constants/theme';

import {

  SunIcon,

  MoonIcon,

  BellIcon,

  SettingsIcon,

  DownloadCloudIcon,

  UserIcon,

  LogoutIcon,

} from './icons';



interface HeaderProps {

  activeView: View;

  userName: string;

  userAvatarUrl?: string | null;

  isDarkMode: boolean;

  onToggleTheme: () => void;

  onLogout: () => void;

  notifications?: Notification[];

  onToggleNotifications?: () => void;

  onOpenOfflineMaps?: () => void;

  onOpenProfile?: () => void;

  onOpenAccount?: () => void;

  onOpenCrewSettings?: () => void;

  onOpenNotificationSettings?: () => void;

  onOpenSupport?: () => void;

  onOpenLegal?: () => void;

  onOpenAdmin?: () => void;

  onOpenHome?: () => void;

}



const Header: React.FC<HeaderProps> = ({

  activeView,

  isDarkMode,

  onToggleTheme,

  onLogout,

  notifications = [],

  onToggleNotifications,

  onOpenOfflineMaps,

  onOpenProfile,

  onOpenAccount,

  onOpenCrewSettings,

  onOpenNotificationSettings,

  onOpenSupport,

  onOpenLegal,

  onOpenAdmin,

}) => {

  const { t } = useTranslation();

  const theme = getRoddoTheme(isDarkMode);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const settingsRef = useRef<HTMLDivElement>(null);

  const headerRef = useRef<HTMLElement>(null);



  const unreadNotificationCount = useMemo(

    () => (notifications || []).filter((n) => !n?.read).length,

    [notifications],

  );



  const hasUnread = unreadNotificationCount > 0;



  const notificationsActive =

    activeView === View.Notifications || activeView === View.NotificationSettings;

  const settingsActive =

    isSettingsOpen ||

    activeView === View.Profile ||

    activeView === View.EditProfile ||

    activeView === View.Account ||

    activeView === View.CrewSettings ||

    activeView === View.Devices ||

    activeView === View.Support ||

    activeView === View.Legal ||

    activeView === View.TermsOfService ||

    activeView === View.PrivacyPolicy ||

    activeView === View.Admin;



  const sectionHeader = useMemo(() => {

    switch (activeView) {

      case View.Map:

        return { title: 'Big Blue', subtitle: 'GLOBAL CHART' };

      case View.Community:

        return { title: 'Baleen Bay', subtitle: 'SOCIAL HUB' };

      case View.Bazaar:

      case View.SellerDashboard:

      case View.PurchaseHistory:

      case View.Receipt:

        return { title: 'Baleen Bay Bazaar', subtitle: 'TRADING POST' };

      case View.LogCatch:

      case View.CatchHistory:

        return { title: 'Roddo Log', subtitle: 'CATCH HISTORY' };

      case View.TackleBox:

      case View.MyPage:

      case (View as any).GearHub:

      case View.LicenseDetail:

        return { title: 'The Hold', subtitle: 'GEAR & STORAGE' };

      case View.Achievements:

        return { title: 'Achievements', subtitle: 'PROGRESS & BADGES' };

      case View.Challenges:

        return { title: 'Challenges', subtitle: 'RANKS & XP' };

      case View.Events:

        return { title: 'Tournaments', subtitle: 'COMPETE & EXPLORE' };

      case View.FishIQ:

        return { title: 'Water Read', subtitle: 'FISHING INTELLIGENCE' };

      case View.Profile:

      case View.EditProfile:

      case View.Account:

      case View.PublicProfile:

        return { title: 'Profile', subtitle: 'SETTINGS' };

      case View.Crew:

      case View.CrewSettings:

        return { title: 'Crew Deck', subtitle: 'PRIVATE BOARDS' };

      case View.Support:

        return { title: 'Support', subtitle: 'SETTINGS' };

      case View.Legal:

        return { title: 'Legal', subtitle: 'SETTINGS' };

      case View.TermsOfService:

        return { title: 'Terms', subtitle: 'SETTINGS' };

      case View.PrivacyPolicy:

        return { title: 'Privacy', subtitle: 'SETTINGS' };

      case View.NotificationSettings:

      case View.Notifications:

        return { title: 'Notifications', subtitle: 'SETTINGS' };

      case View.Devices:

        return { title: 'Devices', subtitle: 'SETTINGS' };

      default:

        return { title: 'Roddo', subtitle: 'POCKET FISHING BUDDY' };

    }

  }, [activeView]);





  useEffect(() => {

    const handleClickOutside = (event: MouseEvent) => {

      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {

        setIsSettingsOpen(false);

      }

    };



    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);

  }, []);



  useLayoutEffect(() => {

    const applyMeasuredHeaderHeight = () => {

      try {

        const el = headerRef.current;

        if (!el) return;



        const next = Math.round(el.getBoundingClientRect().height);

        if (!Number.isFinite(next) || next <= 0) return;



        document.documentElement.style.setProperty('--roddo-header-h', `${next}px`);

      } catch {}

    };



    applyMeasuredHeaderHeight();



    const raf = window.requestAnimationFrame(applyMeasuredHeaderHeight);

    const t1 = window.setTimeout(applyMeasuredHeaderHeight, 80);

    const t2 = window.setTimeout(applyMeasuredHeaderHeight, 220);



    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined' && headerRef.current) {

      try {

        resizeObserver = new ResizeObserver(() => {

          applyMeasuredHeaderHeight();

        });

        resizeObserver.observe(headerRef.current);

      } catch {}

    }



    window.addEventListener('resize', applyMeasuredHeaderHeight);

    window.addEventListener('orientationchange', applyMeasuredHeaderHeight);



    return () => {

      window.cancelAnimationFrame(raf);

      window.clearTimeout(t1);

      window.clearTimeout(t2);

      resizeObserver?.disconnect();

      window.removeEventListener('resize', applyMeasuredHeaderHeight);

      window.removeEventListener('orientationchange', applyMeasuredHeaderHeight);

    };

  }, [activeView, isDarkMode]);



  const iconButtonStyle: React.CSSProperties = {

    background: isDarkMode ? '#121A2B' : '#FFFCF7',

    border: `1px solid ${theme.borderStrong}`,

    color: theme.text,

    boxShadow: isDarkMode

      ? '0 4px 12px rgba(0,0,0,0.18)'

      : '0 4px 10px rgba(89,65,37,0.06)',

  };



  const menuStyle: React.CSSProperties = {

    color: theme.text,

    background: isDarkMode ? '#10182A' : '#FFF9F1',

    border: `1px solid ${theme.borderStrong}`,

    boxShadow: isDarkMode

      ? '0 14px 32px rgba(0,0,0,0.34)'

      : '0 12px 28px rgba(89,65,37,0.12)',

  };



  const menuItemClass =

    'flex items-center justify-between w-full rounded-[12px] px-3 py-3 transition-colors';

  const menuIconClass = 'h-4 w-4';

  const menuTextClass = 'text-sm font-semibold';

  const menuSectionClass = 'px-3 pb-1 pt-2 text-[9px] font-black uppercase tracking-[0.18em]';

  const menuDividerStyle: React.CSSProperties = {

    background: isDarkMode

      ? 'rgba(180,195,220,0.12)'

      : 'rgba(74,58,42,0.12)',

  };



  const menuTone = {

    profile: isDarkMode ? '#AEB8C9' : '#5F6D77',

    crew: isDarkMode ? '#AEB8C9' : '#5F6D77',

    theme: isDarkMode ? '#AEB8C9' : '#5F6D77',

    notifications: isDarkMode ? '#AEB8C9' : '#5F6D77',

    offline: isDarkMode ? '#AEB8C9' : '#5F6D77',

    support: isDarkMode ? '#AEB8C9' : '#5F6D77',

    legal: isDarkMode ? '#AEB8C9' : '#5F6D77',

    admin: isDarkMode ? '#F2B84B' : '#A15B12',

    logout: isDarkMode ? '#EF8B91' : '#D84A4A',

  } as const;



  const menuTextStyle = (color: string): React.CSSProperties => ({

    color,

    textShadow: isDarkMode ? '0 1px 8px rgba(0,0,0,0.22)' : 'none',

  });



  const closeSettingsAnd = (handler?: () => void) => {

    handler?.();

    setIsSettingsOpen(false);

  };



  const headerSurface: React.CSSProperties = {

    color: theme.text,

    background: isDarkMode ? '#0D1322' : '#FFF9F1',

    borderBottom: `1px solid ${isDarkMode ? theme.borderStrong : 'rgba(116,86,54,0.14)'}`,

    boxShadow: isDarkMode

      ? '0 6px 18px rgba(0,0,0,0.22)'

      : '0 6px 16px rgba(89,65,37,0.07)',

  };

const headerClassName =

  activeView === View.Map

    ? 'roddo-final-header roddo-responsive-header relative z-[1050] px-3 min-[390px]:px-4 sm:px-5 lg:px-7'

    : 'roddo-final-header roddo-responsive-header sticky top-0 z-[1050] px-3 min-[390px]:px-4 sm:px-5 lg:px-7';

  return (

    <header

  ref={headerRef}

  className={`${headerClassName} roddo-theme-lock roddo-safe-header`}

  data-roddo-theme={isDarkMode ? 'dark' : 'light'}

  style={{

    ...headerSurface,

    paddingTop: 'var(--roddo-safe-top, 0px)',

  }}

>

      <div

        className="pointer-events-none absolute inset-x-0 bottom-0 h-px"

        style={{

          background: isDarkMode

            ? 'rgba(112,87,232,0.22)'

            : 'rgba(47,128,237,0.16)',

        }}

      />



      <div className="relative z-10 h-[var(--roddo-header-core-h)] w-full">

        <div className="pointer-events-none absolute inset-x-0 top-1/2 z-0 flex -translate-y-[54%] justify-center px-[70px] min-[390px]:px-[78px] sm:px-[118px] lg:px-[190px]">

          <div className="relative h-[46px] w-[168px] max-w-full min-[390px]:h-[50px] min-[390px]:w-[182px] sm:h-[58px] sm:w-[226px] lg:h-[60px] lg:w-[236px]">

            <img

              src="/assets/headersign.png"

              alt=""

              aria-hidden="true"

              draggable={false}

              className="absolute inset-0 h-full w-full object-contain opacity-[0.98] drop-shadow-[0_5px_10px_rgba(0,0,0,0.14)]"

            />

            <div className="absolute inset-0 flex -translate-y-[1px] flex-col items-center justify-center px-5 text-center min-[390px]:px-6">

              <div

                className={`max-w-full whitespace-nowrap font-bold leading-none ${

                  sectionHeader.title === 'Baleen Bay Bazaar'

                    ? 'text-[13px] min-[390px]:text-[15px] sm:text-[18px] lg:text-[19px]'

                    : sectionHeader.title.length >= 16

                      ? 'text-[16px] min-[390px]:text-[18px] sm:text-[22px]'

                      : sectionHeader.title.length >= 12

                        ? 'text-[19px] min-[390px]:text-[21px] sm:text-[25px]'

                        : 'text-[20px] min-[390px]:text-[22px] sm:text-[27px] lg:text-[28px]'

                }`}

                style={{

                  fontFamily: 'var(--roddo-font-sign, var(--roddo-font-ui, system-ui, sans-serif))',

                  fontWeight: 700,

                  color: '#2E1A12',

                  letterSpacing: '0.015em',

                  textShadow:

                    '0 1px 0 rgba(255,214,154,0.22), 0 -1px 0 rgba(47,24,12,0.62), 0 0 1px rgba(36,16,8,0.72)',

                }}

              >

                {sectionHeader.title}

              </div>

              </div>

          </div>

        </div>



        <div className="relative z-10 flex h-full items-center justify-between">

          <div className="flex w-[82px] items-center justify-start min-[390px]:w-[88px] sm:w-[132px] lg:w-[180px]">

            <div

              className="-translate-x-1 -rotate-[9deg]"

              aria-label={t('header.logo', 'Roddo')}

              title={t('header.logo', 'Roddo')}

            >

              <img

                src="/assets/woodensign.png"

                alt={t('header.logo', 'Roddo')}

                draggable={false}

                className="h-[23px] w-auto object-contain drop-shadow-[0_7px_12px_rgba(0,0,0,0.16)] min-[390px]:h-[25px] sm:h-[34px] lg:h-[38px]"

              />

            </div>

          </div>



          <div className="relative flex w-[82px] items-center justify-end gap-1.5 min-[390px]:w-[88px] sm:w-[132px] sm:gap-2">

            {onToggleNotifications ? (

              <button

                type="button"

                onClick={() => onToggleNotifications()}

                className="roddo-tactile-hit relative grid h-9 w-9 place-items-center rounded-[14px] transition-transform hover:-translate-y-0.5 active:scale-95 min-[390px]:h-10 min-[390px]:w-10 sm:h-11 sm:w-11"

                style={iconButtonStyle}

                aria-label={t('header.notifications', 'Notifications')}

                title={t('header.notifications', 'Notifications')}

                id="roddo-header-notifications-button"

              >

                <BellIcon

                  className="relative z-10 h-[18px] w-[18px] sm:h-5 sm:w-5"

                  style={{

                    color: notificationsActive

                      ? isDarkMode

                        ? '#66D9E8'

                        : '#2F80ED'

                      : isDarkMode

                        ? '#8B97AA'

                        : '#71808C',

                  }}

                />



                {hasUnread ? (

                  <span

                    className="pointer-events-none absolute -right-2 -top-2 z-30 h-[18px] min-w-[18px] rounded-full px-1 text-center text-[10px] font-extrabold leading-[18px] text-white shadow-[0_2px_0_rgba(0,0,0,0.35)]"

                    aria-label={`${unreadNotificationCount} unread notifications`}

                    style={{ background: '#ff4d6d' }}

                  >

                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}

                  </span>

                ) : null}

              </button>

            ) : null}



            <div className="relative" ref={settingsRef}>

              <button

                type="button"

                onClick={() => setIsSettingsOpen((prev) => !prev)}

                className="roddo-tactile-hit relative grid h-9 w-9 place-items-center rounded-[14px] transition-transform hover:-translate-y-0.5 active:scale-95 min-[390px]:h-10 min-[390px]:w-10 sm:h-11 sm:w-11"

                style={iconButtonStyle}

                aria-label={t('header.settings', 'Settings')}

                aria-expanded={isSettingsOpen}

                id="roddo-header-settings-button"

              >

                <SettingsIcon

                  className="relative z-10 h-[18px] w-[18px] sm:h-5 sm:w-5"

                  style={{

                    color: settingsActive

                      ? isDarkMode

                        ? '#F2B84B'

                        : '#F1642E'

                      : isDarkMode

                        ? '#8B97AA'

                        : '#71808C',

                  }}

                />

              </button>



              {isSettingsOpen ? (

                <div

                  className="roddo-safe-popover absolute right-0 z-50 mt-2 flex w-[min(252px,calc(100vw-24px))] flex-col gap-1 overflow-y-auto rounded-[18px] p-2.5"

                  style={menuStyle}

                  data-roddo-theme={isDarkMode ? 'dark' : 'light'}

                  role="menu"

                  aria-labelledby="roddo-header-settings-button"

                >

                  <div className={menuSectionClass} style={{ color: theme.textMuted }}>

                    Your world

                  </div>



                  <button

                    type="button"

                    onClick={() => closeSettingsAnd(onOpenProfile)}

                    className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                    style={{ color: theme.text, background: 'transparent' }}

                    role="menuitem"

                  >

                    <div className="flex items-center gap-2.5">

                      <UserIcon className={menuIconClass} style={{ color: menuTone.profile }} />

                      <span className={menuTextClass} style={menuTextStyle(menuTone.profile)}>{t('header.profile', 'Profile')}</span>

                    </div>

                  </button>



                  {onOpenAccount ? (

                    <button

                      type="button"

                      onClick={() => closeSettingsAnd(onOpenAccount)}

                      className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                      style={{ color: theme.text, background: 'transparent' }}

                      role="menuitem"

                    >

                      <div className="flex items-center gap-2.5">

                        <span

                          className="grid h-4 w-4 place-items-center text-[14px] leading-none"

                          aria-hidden="true"

                          style={{ color: menuTone.profile }}

                        >

                          🔐

                        </span>

                        <span className={menuTextClass} style={menuTextStyle(menuTone.profile)}>Account</span>

                      </div>

                    </button>

                  ) : null}



                  {onOpenCrewSettings ? (

                    <>

                      <div className="my-1 h-px" style={menuDividerStyle} />

                      <div className={menuSectionClass} style={{ color: theme.textMuted }}>

                        Crew

                      </div>



                      <button

                        type="button"

                        onClick={() => closeSettingsAnd(onOpenCrewSettings)}

                        className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                        style={{ color: theme.text, background: 'transparent' }}

                        role="menuitem"

                      >

                        <div className="flex items-center gap-2.5">

                          <span

                            className="grid h-4 w-4 place-items-center rounded-full text-[11px] font-black leading-none"

                            aria-hidden="true"

                            style={{

                              color: isDarkMode ? '#AEB8C9' : '#5F6D77',

                              background: 'transparent',

                            }}

                          >

                            ⚓

                          </span>

                          <span className={menuTextClass} style={menuTextStyle(menuTone.crew)}>{t('header.crewSettings', 'Crew settings')}</span>

                        </div>

                      </button>

                    </>

                  ) : null}



                  <div className="my-1 h-px" style={menuDividerStyle} />

                  <div className={menuSectionClass} style={{ color: theme.textMuted }}>

                    Feel

                  </div>



                  <button

                    type="button"

                    onClick={() => closeSettingsAnd(onToggleTheme)}

                    className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                    style={{ color: theme.text, background: 'transparent' }}

                    role="menuitem"

                  >

                    <div className="flex items-center gap-2.5">

                      {isDarkMode ? (

                        <SunIcon className={menuIconClass} style={{ color: menuTone.theme }} />

                      ) : (

                        <MoonIcon className={menuIconClass} style={{ color: menuTone.theme }} />

                      )}

                      <span className={menuTextClass} style={menuTextStyle(menuTone.theme)}>

                        {isDarkMode

                          ? t('header.lightMode', 'Light mode')

                          : t('header.darkMode', 'Dark mode')}

                      </span>

                    </div>

                  </button>



                  {onOpenNotificationSettings ? (

                    <button

                      type="button"

                      onClick={() => closeSettingsAnd(onOpenNotificationSettings)}

                      className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                      style={{ color: theme.text, background: 'transparent' }}

                      role="menuitem"

                    >

                      <div className="flex items-center gap-2.5">

                        <BellIcon className={menuIconClass} style={{ color: menuTone.notifications }} />

                        <span className={menuTextClass} style={menuTextStyle(menuTone.notifications)}>

                          {t('header.notificationSettings', 'Notification settings')}

                        </span>

                      </div>

                    </button>

                  ) : null}



                  {onOpenOfflineMaps ? (

                    <button

                      type="button"

                      onClick={() => closeSettingsAnd(onOpenOfflineMaps)}

                      className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                      style={{ color: theme.text, background: 'transparent' }}

                      role="menuitem"

                    >

                      <div className="flex items-center gap-2.5">

                        <DownloadCloudIcon className={menuIconClass} style={{ color: menuTone.offline }} />

                        <span className={menuTextClass} style={menuTextStyle(menuTone.offline)}>{t('header.offlineMaps', 'Offline maps')}</span>

                      </div>

                    </button>

                  ) : null}



                  {(onOpenSupport || onOpenLegal) ? (

                    <>

                      <div className="my-1 h-px" style={menuDividerStyle} />

                      <div className={menuSectionClass} style={{ color: theme.textMuted }}>

                        Trust + help

                      </div>

                    </>

                  ) : null}



                  {onOpenSupport ? (

                    <button

                      type="button"

                      onClick={() => closeSettingsAnd(onOpenSupport)}

                      className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                      style={{ color: theme.text, background: 'transparent' }}

                      role="menuitem"

                    >

                      <div className="flex items-center gap-2.5">

                        <span

                          className="grid h-4 w-4 place-items-center text-[17px] font-black leading-none"

                          aria-hidden="true"

                          style={{ color: isDarkMode ? '#AEB8C9' : '#5F6D77' }}

                        >

                          ?

                        </span>

                        <span className={menuTextClass} style={menuTextStyle(menuTone.support)}>{t('header.support', 'Support')}</span>

                      </div>

                    </button>

                  ) : null}



                  {onOpenLegal ? (

                    <button

                      type="button"

                      onClick={() => closeSettingsAnd(onOpenLegal)}

                      className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                      style={{ color: theme.text, background: 'transparent' }}

                      role="menuitem"

                    >

                      <div className="flex items-center gap-2.5">

                        <span className="grid h-4 w-4 place-items-center text-[15px] leading-none grayscale opacity-70" aria-hidden="true">⚖️</span>

                        <span className={menuTextClass} style={menuTextStyle(menuTone.legal)}>{t('header.legal', 'Legal')}</span>

                      </div>

                    </button>

                  ) : null}





                  {onOpenAdmin ? (

                    <>

                      <div className="my-1 h-px" style={menuDividerStyle} />

                      <div className={menuSectionClass} style={{ color: theme.textMuted }}>

                        Restricted

                      </div>

                      <button

                        type="button"

                        onClick={() => closeSettingsAnd(onOpenAdmin)}

                        className={`${menuItemClass} hover:bg-black/[.035] dark:hover:bg-white/[.045]`}

                        style={{ color: theme.text, background: 'transparent' }}

                        role="menuitem"

                      >

                        <div className="flex items-center gap-2.5">

                          <span className="grid h-4 w-4 place-items-center text-[14px] leading-none" aria-hidden="true">🛡️</span>

                          <span className={menuTextClass} style={menuTextStyle(menuTone.admin)}>Admin console</span>

                        </div>

                      </button>

                    </>

                  ) : null}



                  <div className="my-1 h-px" style={menuDividerStyle} />

                  <div className={menuSectionClass} style={{ color: theme.textMuted }}>

                    Session

                  </div>



                  <button

                    type="button"

                    onClick={() => closeSettingsAnd(onLogout)}

                    className="flex w-full items-center justify-between rounded-[12px] px-3 py-3 transition-colors hover:bg-black/[.035] dark:hover:bg-white/[.045]"

                    role="menuitem"

                  >

                    <div className="flex items-center gap-2.5" style={{ color: menuTone.logout }}>

                      <LogoutIcon className="h-4 w-4 opacity-95" />

                      <span className="text-sm font-extrabold" style={menuTextStyle(menuTone.logout)}>

                        {t('header.logout', 'Logout')}

                      </span>

                    </div>

                  </button>

                </div>

              ) : null}

            </div>

          </div>

        </div>

      </div>

    </header>

  );

};



export default Header;
